import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { CalendlyButton } from '../components/CalendlyButton';
import { 
  GraduationCap, 
  Truck, 
  ClipboardCheck, 
  Users, 
  BookOpen,
  ArrowRight,
  CheckCircle2,
  Building2,
  Briefcase,
  MapPin,
  Monitor,
  Calendar
} from 'lucide-react';

const Formations = () => {
  return (
    <>
      <Helmet>
        <title>Formateur Polyvalent dans le Vaucluse | Mehdi Richa</title>
        <meta 
          name="description" 
          content="Formateur polyvalent dans le Vaucluse : logistique, qualité, management, CEJM. Interventions en BTS, lycées, entreprises. Formateur logistique Vaucluse, Avignon." 
        />
        <meta property="og:title" content="Formateur Polyvalent Vaucluse - Mehdi Richa" />
        <meta property="og:description" content="Formation professionnelle dans le Vaucluse : logistique, transport, qualité, management. Interventions en établissements scolaires et entreprises." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href={`${window.location.origin}/formateur-polyvalent-vaucluse`} />
      </Helmet>

      {/* Hero Section */}
      <section className="relative bg-slate-50 overflow-hidden" data-testid="formations-hero">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-slate-50" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in">
              <span className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-6">
                Formation professionnelle
              </span>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight leading-tight mb-6">
                Formateur polyvalent dans le Vaucluse
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed mb-8 max-w-xl">
                <strong>Formateur qualité Vaucluse</strong>, je suis également spécialisé en logistique, 
                management et économie-gestion. J'interviens aussi bien auprès des apprenants (lycéens, 
                étudiants en BTS) que des professionnels en entreprise.
              </p>
              <Button 
                asChild 
                size="lg"
                className="rounded-full bg-blue-600 hover:bg-blue-700 px-8 py-6 text-base"
                data-testid="formations-hero-cta"
              >
                <a href="mailto:mehdi.richa.formation@gmail.com?subject=Projet de formation">
                  Construire une formation sur mesure
                  <ArrowRight className="ml-2 h-5 w-5" />
                </a>
              </Button>
            </div>
            <div className="animate-fade-in animation-delay-200 hidden lg:block">
              <div className="relative">
                <div className="absolute -inset-4 bg-blue-600/10 rounded-3xl transform rotate-3" />
                <img
                  src="https://images.unsplash.com/photo-1758691736067-b309ee3ef7b9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBjb3Jwb3JhdGUlMjB0cmFpbmluZyUyMHdvcmtzaG9wfGVufDB8fHx8MTc2OTk0NzcxMHww&ixlib=rb-4.1.0&q=85"
                  alt="Formation professionnelle en entreprise"
                  className="relative rounded-2xl shadow-2xl w-full h-auto object-cover"
                  loading="eager"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Training Domains */}
      <section className="py-20 md:py-28 bg-white" data-testid="formations-domaines">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-16 animate-fade-in">
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Mes domaines de formation
            </h2>
            <p className="text-slate-600">
              Une expertise variée pour répondre à différents besoins de formation dans le Vaucluse
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Logistique */}
            <Card className="bg-white border border-slate-100 shadow-sm hover:shadow-lg transition-shadow duration-300 overflow-hidden animate-fade-in animation-delay-100" data-testid="domaine-logistique">
              <CardContent className="p-8">
                <div className="w-14 h-14 mb-6 bg-blue-600 rounded-2xl flex items-center justify-center">
                  <Truck className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-serif text-xl font-bold text-slate-900 mb-3">
                  Logistique & Transport
                </h3>
                <p className="text-slate-600 leading-relaxed mb-4">
                  <strong>Formateur logistique Vaucluse</strong>, j'interviens sur l'ensemble des thématiques 
                  liées à la supply chain, au transport et à la gestion des flux.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-slate-700 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    BTS GTLA (Gestion des Transports et Logistique Associée)
                  </li>
                  <li className="flex items-center gap-2 text-slate-700 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    Logistique urbaine et dernier kilomètre
                  </li>
                  <li className="flex items-center gap-2 text-slate-700 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    Gestion des stocks et approvisionnements
                  </li>
                  <li className="flex items-center gap-2 text-slate-700 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    Organisation des flux et optimisation
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Qualité */}
            <Card className="bg-white border border-slate-100 shadow-sm hover:shadow-lg transition-shadow duration-300 overflow-hidden animate-fade-in animation-delay-200" data-testid="domaine-qualite">
              <CardContent className="p-8">
                <div className="w-14 h-14 mb-6 bg-blue-600 rounded-2xl flex items-center justify-center">
                  <ClipboardCheck className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-serif text-xl font-bold text-slate-900 mb-3">
                  Qualité & Amélioration continue
                </h3>
                <p className="text-slate-600 leading-relaxed mb-4">
                  Formation aux démarches qualité, aux outils d'amélioration continue et aux 
                  référentiels de certification.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-slate-700 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    Fondamentaux de la qualité
                  </li>
                  <li className="flex items-center gap-2 text-slate-700 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    Outils qualité (PDCA, 5S, Ishikawa...)
                  </li>
                  <li className="flex items-center gap-2 text-slate-700 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    Sensibilisation aux normes ISO
                  </li>
                  <li className="flex items-center gap-2 text-slate-700 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    Démarche d'amélioration continue
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Management */}
            <Card className="bg-white border border-slate-100 shadow-sm hover:shadow-lg transition-shadow duration-300 overflow-hidden animate-fade-in animation-delay-300" data-testid="domaine-management">
              <CardContent className="p-8">
                <div className="w-14 h-14 mb-6 bg-blue-600 rounded-2xl flex items-center justify-center">
                  <Users className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-serif text-xl font-bold text-slate-900 mb-3">
                  Management & Organisation
                </h3>
                <p className="text-slate-600 leading-relaxed mb-4">
                  Formation au management d'équipe, à l'organisation du travail et à la 
                  gestion de projet.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-slate-700 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    Fondamentaux du management
                  </li>
                  <li className="flex items-center gap-2 text-slate-700 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    Animation d'équipe
                  </li>
                  <li className="flex items-center gap-2 text-slate-700 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    Gestion du temps et des priorités
                  </li>
                  <li className="flex items-center gap-2 text-slate-700 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    Conduite de projet
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* CEJM */}
            <Card className="bg-white border border-slate-100 shadow-sm hover:shadow-lg transition-shadow duration-300 overflow-hidden animate-fade-in animation-delay-400" data-testid="domaine-cejm">
              <CardContent className="p-8">
                <div className="w-14 h-14 mb-6 bg-blue-600 rounded-2xl flex items-center justify-center">
                  <BookOpen className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-serif text-xl font-bold text-slate-900 mb-3">
                  CEJM / Économie-Gestion
                </h3>
                <p className="text-slate-600 leading-relaxed mb-4">
                  Enseignement en Culture Économique, Juridique et Managériale pour les filières 
                  tertiaires (STMG, BTS tertiaires).
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-slate-700 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    CEJM en BTS tertiaires
                  </li>
                  <li className="flex items-center gap-2 text-slate-700 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    Économie-gestion en lycée
                  </li>
                  <li className="flex items-center gap-2 text-slate-700 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    Droit et management des entreprises
                  </li>
                  <li className="flex items-center gap-2 text-slate-700 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    Préparation aux examens
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Publics & Contexts */}
      <section className="py-20 md:py-28 bg-slate-50" data-testid="formations-publics">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-16 animate-fade-in">
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Publics & Contextes d'intervention
            </h2>
            <p className="text-slate-600">
              Des interventions adaptées à chaque profil et chaque structure
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Card className="bg-white border-0 shadow-sm hover:shadow-md transition-shadow duration-300 animate-fade-in animation-delay-100" data-testid="public-lycees">
              <CardContent className="p-6">
                <div className="w-12 h-12 mb-4 bg-blue-100 rounded-xl flex items-center justify-center">
                  <GraduationCap className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="font-semibold text-lg text-slate-900 mb-2">Lycées & BTS</h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Interventions en établissements scolaires : STMG, BTS GTLA, BTS tertiaires. 
                  Cours, préparation aux examens, projets tutorés.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white border-0 shadow-sm hover:shadow-md transition-shadow duration-300 animate-fade-in animation-delay-200" data-testid="public-cfa">
              <CardContent className="p-6">
                <div className="w-12 h-12 mb-4 bg-blue-100 rounded-xl flex items-center justify-center">
                  <BookOpen className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="font-semibold text-lg text-slate-900 mb-2">CFA & Alternance</h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Formation des apprentis dans le cadre de leur cursus en alternance. 
                  Lien théorie-pratique, suivi individualisé.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white border-0 shadow-sm hover:shadow-md transition-shadow duration-300 animate-fade-in animation-delay-300" data-testid="public-of">
              <CardContent className="p-6">
                <div className="w-12 h-12 mb-4 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="font-semibold text-lg text-slate-900 mb-2">Organismes de formation</h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Interventions pour le compte d'organismes de formation professionnelle continue. 
                  Modules spécifiques ou parcours complets.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white border-0 shadow-sm hover:shadow-md transition-shadow duration-300 animate-fade-in animation-delay-400" data-testid="public-entreprises">
              <CardContent className="p-6">
                <div className="w-12 h-12 mb-4 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Briefcase className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="font-semibold text-lg text-slate-900 mb-2">Entreprises</h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Formations intra-entreprise sur mesure pour vos équipes. 
                  Montée en compétences, perfectionnement, sensibilisation.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white border-0 shadow-sm hover:shadow-md transition-shadow duration-300 animate-fade-in animation-delay-100" data-testid="public-collectivites">
              <CardContent className="p-6">
                <div className="w-12 h-12 mb-4 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="font-semibold text-lg text-slate-900 mb-2">Collectivités</h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Accompagnement des agents des collectivités territoriales dans leurs 
                  besoins de formation qualité et management.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white border-0 shadow-sm hover:shadow-md transition-shadow duration-300 animate-fade-in animation-delay-200" data-testid="public-ecoles">
              <CardContent className="p-6">
                <div className="w-12 h-12 mb-4 bg-blue-100 rounded-xl flex items-center justify-center">
                  <GraduationCap className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="font-semibold text-lg text-slate-900 mb-2">Écoles supérieures</h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Interventions ponctuelles en écoles de commerce ou d'ingénieurs sur des 
                  modules spécifiques logistique ou qualité.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Pedagogy & Formats */}
      <section className="py-20 md:py-28 bg-white" data-testid="formations-pedagogie">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Pédagogie */}
            <div className="animate-fade-in">
              <h2 className="font-serif text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
                Une pédagogie ancrée dans le réel
              </h2>
              <p className="text-slate-600 leading-relaxed mb-6">
                Mon approche pédagogique privilégie l'apprentissage par la pratique. Je m'appuie 
                sur des cas concrets, des mises en situation et des exemples issus du terrain pour 
                rendre les contenus vivants et directement applicables.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl">
                  <CheckCircle2 className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-slate-900">Cas pratiques</h3>
                    <p className="text-slate-600 text-sm">Études de cas réels pour ancrer les apprentissages</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl">
                  <CheckCircle2 className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-slate-900">Mises en situation</h3>
                    <p className="text-slate-600 text-sm">Exercices pratiques et jeux de rôle</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl">
                  <CheckCircle2 className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-slate-900">Supports clairs</h3>
                    <p className="text-slate-600 text-sm">Documents synthétiques et visuels à conserver</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl">
                  <CheckCircle2 className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-slate-900">Interactivité</h3>
                    <p className="text-slate-600 text-sm">Échanges et questions pour une formation vivante</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Formats */}
            <div className="animate-fade-in animation-delay-200">
              <h2 className="font-serif text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
                Formats d'intervention
              </h2>
              <div className="space-y-6">
                <Card className="bg-slate-50 border-0">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Calendar className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900 mb-2">Modules courts</h3>
                        <p className="text-slate-600 text-sm leading-relaxed">
                          Formations d'une demi-journée à quelques jours pour traiter un sujet 
                          spécifique ou sensibiliser vos équipes.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-slate-50 border-0">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
                        <BookOpen className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900 mb-2">Parcours longs</h3>
                        <p className="text-slate-600 text-sm leading-relaxed">
                          Accompagnement sur plusieurs semaines ou mois dans le cadre d'un cursus 
                          diplômant ou d'un programme de formation structuré.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-slate-50 border-0">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900 mb-2">Présentiel (Vaucluse)</h3>
                        <p className="text-slate-600 text-sm leading-relaxed">
                          Interventions sur site dans le Vaucluse : Avignon, Carpentras, Orange, 
                          Cavaillon et communes environnantes.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-slate-50 border-0">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Monitor className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900 mb-2">Distanciel</h3>
                        <p className="text-slate-600 text-sm leading-relaxed">
                          Formations en visioconférence pour les structures éloignées ou les 
                          modules adaptés au format à distance.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-slate-900" data-testid="formations-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl sm:text-4xl font-bold text-white mb-6 animate-fade-in">
            Un projet de formation ?
          </h2>
          <p className="text-slate-300 text-lg mb-10 animate-fade-in animation-delay-100">
            Discutons ensemble de vos besoins. Je vous propose un premier échange gratuit 
            pour comprendre votre contexte et construire une formation adaptée.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in animation-delay-200">
            <CalendlyButton 
              size="lg"
              className="rounded-full bg-white text-slate-900 hover:bg-slate-100 px-10 py-6 text-lg"
            >
              Prendre rendez-vous
            </CalendlyButton>
            <Button 
              asChild 
              variant="outline"
              size="lg"
              className="rounded-full border-white text-white hover:bg-white/10 px-10 py-6 text-lg"
              data-testid="formations-cta-button"
            >
              <a href="mailto:mehdi.richa.formation@gmail.com?subject=Projet de formation">
                M'envoyer un email
                <ArrowRight className="ml-2 h-5 w-5" />
              </a>
            </Button>
          </div>
        </div>
      </section>
    </>
  );
};

export default Formations;
